import { config } from "../config";
import { createApiClient } from "./client";
import type {
  AppendFleetKeysResultDto,
  BatchDeleteFleetInstancesResultDto,
  CreateFleetInstanceRequest,
  CreateFleetInstancesResultDto,
  DeleteFleetInstanceResultDto,
  FleetApiKeyEntry,
  FleetInstanceCredentialsDto,
  FleetInstanceViewDto,
  FleetStatsDto,
  ListFleetInstancesFilters,
  ListFleetInstancesResultDto,
  MarkFleetRecoveredResultDto,
  PurgeDeletedFleetInstancesResultDto,
} from "./types";

/**
 * Kiro-Go 舰队管理器的 API 客户端(design.md > API 一览表)。
 * Fleet Manager 拥有自己独立的 API Gateway RestApi(`kiro-go-fleet-api`,
 * 见 infra/lib/fleet-stack.ts),base URL 与既有平台的 `apiClient` 不同,
 * 因此用 `config.fleetApiBaseUrl` 构造一个独立的客户端实例,但鉴权机制
 * (Cognito IdToken)与既有 `apiClient` 完全一致(见 api/client.ts)。
 */
const fleetApiClient = createApiClient(config.fleetApiBaseUrl);

/** POST /fleet/instances —— 创建(单条=长度1的批量,批量上限500,Requirement 1)。 */
export function createInstances(records: CreateFleetInstanceRequest[]) {
  return fleetApiClient.post<CreateFleetInstancesResultDto>("/fleet/instances", { records });
}

/** GET /fleet/instances —— 列表分页/检索/筛选(Requirement 6,需求⑧新增邮箱/公网IP筛选)。 */
export function listInstances(filters: ListFleetInstancesFilters = {}) {
  return fleetApiClient.get<ListFleetInstancesResultDto>("/fleet/instances", {
    search: filters.search,
    status: filters.status,
    region: filters.region,
    expiry_from: filters.expiryFrom,
    expiry_to: filters.expiryTo,
    kiro_user_email: filters.kiroUserEmail,
    public_ip: filters.publicIp,
    page: filters.page,
    page_size: filters.pageSize,
  });
}

/** GET /fleet/instances/{instanceId} —— 详情(不含明文凭证,Requirement 5/7)。 */
export function getInstance(instanceId: string) {
  return fleetApiClient.get<FleetInstanceViewDto>(`/fleet/instances/${encodeURIComponent(instanceId)}`);
}

/** GET /fleet/instances/{instanceId}/credentials —— 明文凭证(仅 Admin,Requirement 5.3/5.5/12.2)。 */
export function getCredentials(instanceId: string) {
  return fleetApiClient.get<FleetInstanceCredentialsDto>(
    `/fleet/instances/${encodeURIComponent(instanceId)}/credentials`
  );
}

/** POST /fleet/instances/{instanceId}/api-keys —— 充值(追加1-100个Key,Requirement 4)。
 *  可选 expiresAt(YYYY-MM-DD):本次充值到期日,会打到每个新增 Key 上并刷新实例到期日。 */
export function appendKeys(instanceId: string, entries: FleetApiKeyEntry[], expiresAt?: string) {
  return fleetApiClient.post<AppendFleetKeysResultDto>(
    `/fleet/instances/${encodeURIComponent(instanceId)}/api-keys`,
    { kiro_api_keys: entries, expires_at: expiresAt }
  );
}

/** POST /fleet/instances/{instanceId}/mark-recovered —— 标记已手动恢复凭证(Requirement 10.6-10.8)。 */
export function markRecovered(instanceId: string, newPassword: string) {
  return fleetApiClient.post<MarkFleetRecoveredResultDto>(
    `/fleet/instances/${encodeURIComponent(instanceId)}/mark-recovered`,
    { new_password: newPassword }
  );
}

/** DELETE /fleet/instances/{instanceId} —— 单条删除(Requirement 6)。 */
export function deleteInstance(instanceId: string) {
  return fleetApiClient.delete<DeleteFleetInstanceResultDto>(
    `/fleet/instances/${encodeURIComponent(instanceId)}`
  );
}

/** POST /fleet/instances/batch-delete —— 批量删除(≤500,二次确认由前端负责,Requirement 6.5-6.8)。 */
export function batchDeleteInstances(instanceIds: string[]) {
  return fleetApiClient.post<BatchDeleteFleetInstancesResultDto>("/fleet/instances/batch-delete", {
    instance_ids: instanceIds,
  });
}

/** 需求⑦:POST /fleet/instances/purge-deleted —— 批量清除状态为 DELETED 的实例记录本身。 */
export function purgeDeletedInstances(instanceIds: string[]) {
  return fleetApiClient.post<PurgeDeletedFleetInstancesResultDto>("/fleet/instances/purge-deleted", {
    instance_ids: instanceIds,
  });
}

/** GET /fleet/stats —— 统计看板(Requirement 9)。 */
export function getStats() {
  return fleetApiClient.get<FleetStatsDto>("/fleet/stats");
}

/** POST /fleet/instances/{instanceId}/resend-notification —— 重新发送通知邮件。
 *  sections 可选:勾选要包含的内容段落(不传=创建成功邮件的默认段落)。 */
export function resendNotification(instanceId: string, sections?: string[]) {
  return fleetApiClient.post<{ instance_id: string; sent: boolean }>(
    `/fleet/instances/${encodeURIComponent(instanceId)}/resend-notification`,
    sections && sections.length > 0 ? { sections } : {}
  );
}
export function updateExpiresAt(instanceId: string, expiresAt: string) {
  return fleetApiClient.patch<{ instance_id: string; expires_at: string }>(
    `/fleet/instances/${encodeURIComponent(instanceId)}/expires-at`,
    { expires_at: expiresAt }
  );
}
